"use client";

import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useDialKit, type DialConfig } from "dialkit";
import {
  motion,
  type Transition,
  useAnimationControls,
  useReducedMotion,
} from "motion/react";

interface PageTransitionProps {
  children: ReactNode;
}

const pageTransitionConfig = {
  enabled: true,
  offsetY: [20, -80, 120, 1],
  blur: [0, 0, 24, 1],
  initialOpacity: [0, 0, 1, 0.05],
  spring: {
    type: "spring",
    visualDuration: 0.6,
    bounce: 0.08,
  },
  replay: {
    type: "action",
    label: "Replay transition",
  },
} satisfies DialConfig;

const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const pathname = usePathname();
  const controls = useAnimationControls();
  const shouldReduceMotion = useReducedMotion();
  const [replayToken, setReplayToken] = useState(0);

  const handleAction = useCallback((action: string) => {
    if (action === "replay") {
      setReplayToken((token) => token + 1);
    }
  }, []);

  const transition = useDialKit(
    "Page transition",
    pageTransitionConfig,
    {
      id: "portfolio-page-transition",
      persist: true,
      onAction: handleAction,
      shortcuts: {
        offsetY: { key: "y", mode: "fine" },
        blur: { key: "b", mode: "fine" },
      },
    },
  );

  const motionTransition = transition.spring as Transition;
  const transitionSignature = useMemo(
    () => JSON.stringify(motionTransition),
    [motionTransition],
  );

  useEffect(() => {
    if (shouldReduceMotion || !transition.enabled) {
      controls.set({ opacity: 1, y: 0, filter: "none" });
      return;
    }

    let isActive = true;

    controls.set({
      opacity: transition.initialOpacity,
      y: transition.offsetY,
      filter: `blur(${transition.blur}px)`,
    });

    void controls
      .start(
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
        },
        motionTransition,
      )
      .then(() => {
        if (isActive) {
          controls.set({ opacity: 1, y: 0, filter: "none" });
        }
      });

    return () => {
      isActive = false;
      controls.stop();
    };
  }, [
    controls,
    motionTransition,
    pathname,
    replayToken,
    shouldReduceMotion,
    transition.blur,
    transition.enabled,
    transition.initialOpacity,
    transition.offsetY,
    transitionSignature,
  ]);

  return (
    <motion.div
      className="min-h-screen"
      initial={{
        opacity: transition.initialOpacity,
        y: transition.offsetY,
        filter: `blur(${transition.blur}px)`,
      }}
      animate={controls}
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;
